import api from '../../utils/api';
import * as util from '../../utils/util';
import { shouldForceLogout } from '../../utils/authCheck';
import {
  UseResource,
  createCachedResource,
  useCachedResource,
  userFacingError,
} from '../../utils/cachedResource';

/**
 * 4대 질병 건강 문진 — 문항 정의 · 응답 인코딩 · AP_009_001 전송
 *
 * 출처
 *  - 문항 원문: `4대 질병 문진.txt`
 *  - API 스펙 : Notion "APP - DT"
 *    https://skillful-tuba-487.notion.site/APP-DT-3b1c8bb3498280439dd8e1e74f4729c1
 *
 * 이 파일이 DTO 계약의 단일 출처다. 문항을 추가·수정할 때는
 *  (1) code 가 서버 문항 코드와 일치하는지
 *  (2) options 의 value 가 스펙의 정수 인코딩과 일치하는지
 * 두 가지를 반드시 함께 확인할 것. 화면 표시 번호(no)와 서버 코드(code)는 별개다.
 */

/** AP_009_001 요청 본문의 질환 키 */
export type DiseaseKey = 'diabetes' | 'hypertension' | 'dyslipidemia' | 'obesity';

export type SurveyOption = {
  /** 서버로 전송되는 정수값 */
  value: number;
  label: string;
};

export type SurveyQuestion = {
  /** 서버 문항 코드. DTO 의 키가 된다 ('1', '21', '31' …) */
  code: string;
  /** 사용자에게 보여줄 번호 ('1', '2-1' …) */
  no: string;
  text: string;
  options: SurveyOption[];
};

/** 대분류 번호 하나에 묶이는 문항 묶음 (2-1, 2-2, 2-3 → groupNo 2) */
export type SurveyGroup = {
  groupNo: number;
  questions: SurveyQuestion[];
};

export type DiseaseSection = {
  key: DiseaseKey;
  /** 탭·헤더 표기명 */
  label: string;
  groups: SurveyGroup[];
};

/** 질환 → { 문항코드: 선택값 } */
export type SurveyAnswers = Record<DiseaseKey, Record<string, number>>;

/** AP_009_001 요청 본문 */
export type DiseaseSurveyRequest = {
  surveyVersion: number;
} & Record<DiseaseKey, Record<string, number>>;

/** 스펙상 현재 문진은 1 고정 */
export const SURVEY_VERSION = 1;

// ── 공용 선택지 ────────────────────────────────────────────────
// 값은 Notion 스펙의 인코딩을 그대로 따른다. 순서를 바꾸면 의미가 바뀐다.

/** 0=예, 1=아니오 */
const YES_NO: SurveyOption[] = [
  { value: 0, label: '예' },
  { value: 1, label: '아니오' },
];

/** 0=있음, 1=없음, 2=모름 — 가족력 문항 */
const FAMILY_HISTORY: SurveyOption[] = [
  { value: 0, label: '있음' },
  { value: 1, label: '없음' },
  { value: 2, label: '모름' },
];

/** 0~4 리커트 5점 */
const LIKERT_5: SurveyOption[] = [
  { value: 0, label: '전혀 그렇지 않다' },
  { value: 1, label: '그렇지 않은 편이다' },
  { value: 2, label: '보통이다' },
  { value: 3, label: '그런 편이다' },
  { value: 4, label: '매우 그렇다' },
];

// ── 문항 정의 ──────────────────────────────────────────────────

export const DISEASE_SURVEY: DiseaseSection[] = [
  {
    key: 'diabetes',
    label: '당뇨병',
    groups: [
      {
        groupNo: 1,
        questions: [
          {
            code: '1',
            no: '1',
            text: '부모 또는 형제자매 중 당뇨병을 진단받은 분이 있습니까?',
            options: FAMILY_HISTORY,
          },
        ],
      },
      {
        groupNo: 2,
        questions: [
          {
            code: '21',
            no: '2-1',
            text: '최근 평소보다 갈증을 자주 느끼거나 물을 많이 마십니까?',
            options: YES_NO,
          },
          {
            code: '22',
            no: '2-2',
            text: '최근 소변을 보는 횟수나 양이 평소보다 증가했습니까?',
            options: YES_NO,
          },
          {
            code: '23',
            no: '2-3',
            text: '최근 충분히 쉬어도 피로감이 지속되거나 쉽게 피곤해집니까?',
            options: YES_NO,
          },
        ],
      },
      {
        groupNo: 3,
        questions: [
          {
            code: '31',
            no: '3-1',
            text: '최근 스트레스를 심하게 느끼고 있습니까?',
            options: LIKERT_5,
          },
          {
            code: '32',
            no: '3-2',
            text: '최근 우울하거나 의욕이 저하된 상태가 자주 지속됩니까?',
            options: LIKERT_5,
          },
        ],
      },
    ],
  },
  {
    key: 'hypertension',
    label: '고혈압',
    groups: [
      {
        groupNo: 1,
        questions: [
          {
            code: '1',
            no: '1',
            text: '부모 또는 형제자매 중 고혈압을 진단받은 분이 있습니까?',
            options: FAMILY_HISTORY,
          },
        ],
      },
      {
        groupNo: 2,
        questions: [
          {
            code: '2',
            no: '2',
            text: '부모 또는 형제자매 중 뇌졸중이나 심근경색을 진단받은 분이 있습니까?',
            options: FAMILY_HISTORY,
          },
        ],
      },
      {
        groupNo: 3,
        questions: [
          {
            code: '31',
            no: '3-1',
            text: '최근 두통이 반복적으로 발생합니까?',
            options: YES_NO,
          },
          {
            code: '32',
            no: '3-2',
            text: '최근 어지럼증이 반복적으로 발생합니까?',
            options: YES_NO,
          },
        ],
      },
      {
        groupNo: 4,
        questions: [
          {
            code: '41',
            no: '4-1',
            text: '최근 가슴이 아프거나 압박되는 듯한 흉통을 느낀 적이 있습니까?',
            options: YES_NO,
          },
          {
            code: '42',
            no: '4-2',
            text: '최근 일상적인 활동 중 숨이 차거나 호흡이 곤란한 적이 있습니까?',
            options: YES_NO,
          },
        ],
      },
    ],
  },
  {
    key: 'dyslipidemia',
    label: '이상지질혈증',
    groups: [
      {
        groupNo: 1,
        questions: [
          {
            code: '1',
            no: '1',
            text: '부모 또는 형제자매 중 고지혈증 또는 이상지질혈증을 진단받은 분이 있습니까?',
            options: FAMILY_HISTORY,
          },
        ],
      },
      {
        groupNo: 2,
        questions: [
          {
            code: '2',
            no: '2',
            text: '부모 또는 형제자매 중 남성은 만 55세 이전, 여성은 만 65세 이전에 심근경색이나 뇌졸중을 진단받은 분이 있습니까?',
            options: FAMILY_HISTORY,
          },
        ],
      },
      {
        groupNo: 3,
        questions: [
          {
            code: '3',
            no: '3',
            text: '현재 콜레스테롤이나 중성지방을 낮추기 위한 약을 복용하고 있습니까?',
            // 0=현재 복용 중, 1=과거 복용, 2=복용한 적 없음, 3=모름
            options: [
              { value: 0, label: '현재 복용 중' },
              { value: 1, label: '과거에 복용했으나 현재는 복용하지 않음' },
              { value: 2, label: '복용한 적 없음' },
              { value: 3, label: '모름' },
            ],
          },
        ],
      },
      {
        groupNo: 4,
        questions: [
          {
            code: '41',
            no: '4-1',
            text: '최근 가슴이 아프거나 압박되는 듯한 흉통을 느낀 적이 있습니까?',
            options: YES_NO,
          },
          {
            code: '42',
            no: '4-2',
            text: '걷기, 계단 오르기 또는 운동 중 평소보다 심하게 숨이 찹니까?',
            options: YES_NO,
          },
        ],
      },
    ],
  },
  {
    key: 'obesity',
    label: '비만',
    groups: [
      {
        groupNo: 1,
        questions: [
          {
            code: '1',
            no: '1',
            text: '최근 6개월 동안 체중이 얼마나 증가했습니까?',
            // 0=증가하지 않음, 1=3kg 미만 증가, 2=3kg 이상 증가, 3=모름
            options: [
              { value: 0, label: '증가하지 않음' },
              { value: 1, label: '3kg 미만 증가' },
              { value: 2, label: '3kg 이상 증가' },
              { value: 3, label: '모름' },
            ],
          },
        ],
      },
      {
        groupNo: 2,
        questions: [
          {
            code: '21',
            no: '2-1',
            text: '최근 충분히 쉬어도 피로감이 지속됩니까?',
            options: YES_NO,
          },
          {
            code: '22',
            no: '2-2',
            text: '최근 신체 활동량이 이전보다 줄었습니까?',
            options: YES_NO,
          },
        ],
      },
      {
        groupNo: 3,
        questions: [
          {
            code: '31',
            no: '3-1',
            text: '최근 무릎 통증으로 걷거나 운동하는 데 불편함이 있습니까?',
            options: YES_NO,
          },
          {
            code: '32',
            no: '3-2',
            text: '최근 허리 통증으로 걷거나 운동하는 데 불편함이 있습니까?',
            options: YES_NO,
          },
        ],
      },
      {
        groupNo: 4,
        questions: [
          {
            code: '4',
            no: '4',
            text: '식사 조절이나 운동을 시도했지만 체중 감량 후 다시 증가하는 일이 반복되었습니까?',
            // 0=예, 1=아니오, 2=체중 조절을 시도한 적 없음
            options: [
              { value: 0, label: '예' },
              { value: 1, label: '아니오' },
              { value: 2, label: '체중 조절을 시도한 적 없음' },
            ],
          },
        ],
      },
      {
        groupNo: 5,
        questions: [
          {
            code: '51',
            no: '5-1',
            text: '짧은 시간에 평소보다 많은 음식을 먹고, 먹는 것을 멈추기 어렵다고 느낀 적이 있습니까?',
            options: YES_NO,
          },
          {
            code: '52',
            no: '5-2',
            text: '스트레스, 우울감, 불안 또는 화가 날 때 배가 고프지 않아도 음식을 먹습니까?',
            options: YES_NO,
          },
        ],
      },
    ],
  },
];

// ── 헬퍼 ──────────────────────────────────────────────────────

/** 빈 응답 객체. 질환 키가 하나라도 빠지면 DTO 가 깨지므로 여기서 만들어 쓴다. */
export const createEmptyAnswers = (): SurveyAnswers =>
  DISEASE_SURVEY.reduce((acc, section) => {
    acc[section.key] = {};
    return acc;
  }, {} as SurveyAnswers);

/** 질환 하나의 전체 문항 (그룹 평탄화) */
export const flattenQuestions = (section: DiseaseSection): SurveyQuestion[] =>
  section.groups.flatMap((group) => group.questions);

/** 질환별 답변 수 / 전체 문항 수 */
export const countAnswered = (section: DiseaseSection, answers: SurveyAnswers) => {
  const questions = flattenQuestions(section);
  const given = answers[section.key] ?? {};
  return {
    answered: questions.filter((q) => given[q.code] !== undefined).length,
    total: questions.length,
  };
};

/** 전 질환 합계 */
export const countAllAnswered = (answers: SurveyAnswers) =>
  DISEASE_SURVEY.reduce(
    (acc, section) => {
      const { answered, total } = countAnswered(section, answers);
      return { answered: acc.answered + answered, total: acc.total + total };
    },
    { answered: 0, total: 0 },
  );

/** 미응답 문항 목록 — 저장 전 검증과 안내 문구에 함께 쓴다. */
export const findUnanswered = (answers: SurveyAnswers) =>
  DISEASE_SURVEY.flatMap((section) => {
    const given = answers[section.key] ?? {};
    return flattenQuestions(section)
      .filter((q) => given[q.code] === undefined)
      .map((q) => ({ diseaseKey: section.key, diseaseLabel: section.label, no: q.no }));
  });

/**
 * AP_009_001 요청 본문 생성.
 * 서버는 문항 코드를 문자열 키로 받으므로 정렬·가공하지 않고 그대로 담는다.
 */
export const buildSurveyPayload = (answers: SurveyAnswers): DiseaseSurveyRequest => {
  const payload = { surveyVersion: SURVEY_VERSION } as DiseaseSurveyRequest;
  DISEASE_SURVEY.forEach((section) => {
    const given = answers[section.key] ?? {};
    payload[section.key] = flattenQuestions(section).reduce((acc, q) => {
      if (given[q.code] !== undefined) acc[q.code] = given[q.code];
      return acc;
    }, {} as Record<string, number>);
  });
  return payload;
};

/**
 * POST app/api/v1/AP_009_001 — 건강 문진 저장
 *
 * 헤더 `X-CUSTOMER-ID` 는 customerId(= localStorage.userCusID).
 * 같은 도메인의 AP_009_002(질환·장기 DT 호출)는 familyId 를 넣는 규약이라 서로 다르다.
 *
 * @returns 서버가 발급한 설문 DB ID
 */
/** 문진 저장·조회의 주체 — 캐시 키로도 쓰인다 */
const currentCustomerId = () => window.localStorage.getItem('userCusID') ?? '';

export const submitDiseaseSurvey = async (answers: SurveyAnswers): Promise<number> => {
  const customerId = currentCustomerId();
  if (customerId === '') throw userFacingError('로그인 정보가 없습니다.');

  const res = await api.post('app/api/v1/AP_009_001', buildSurveyPayload(answers), {
    headers: { 'X-CUSTOMER-ID': customerId },
  });

  // { success, message, data: <설문 DB ID> }
  if (res.data?.success !== true) {
    // 서버 message 를 화면에 그대로 쓰지 않는다 — 실패 응답의 message 에 내부 식별자가
    // 담기는 경우가 확인됐다(AP_009_002 는 못 찾은 familyId 를 message 에 넣는다).
    // 진단은 콘솔로 남기고 사용자에게는 안전한 문구를 보여준다.
    console.warn('[DigitalTwin] AP_009_001 실패 응답:', res.data);
    throw userFacingError('문진 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
  }
  return res.data.data as number;
};

// ── 문진 제출 여부 (permission/isAuth) ────────────────────────

export type SurveyStatus = {
  /** isAuth 가 이 기기에 저장된 계정으로 인증된 프로필을 돌려줬는지 */
  authenticated: boolean;
  /**
   * 제출한 문진의 DB ID.
   *   number → 제출 완료
   *   null   → 미제출
   *   undefined → 판정 불가 (아래 authenticated 와 함께 봐야 한다)
   */
  questionnaireId: number | null | undefined;
};

/** questionnaire 값 정규화. 서버가 숫자/문자열 어느 쪽으로 줘도 받는다. */
const toQuestionnaireId = (v: unknown): number | null | undefined => {
  if (v === undefined) return undefined;
  if (v === null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * POST permission/isAuth — 문진 제출 여부 조회
 *
 * 문진은 아이디 당 1회이므로 판정 근거가 서버에 있어야 한다(기기·스토리지와 무관해야 함).
 * 백엔드 합의: isAuth 응답에 `questionnaire` 를 추가해 제출 시 설문 ID, 미제출이면 null.
 *
 * isAuth 응답은 `data` 래퍼 없는 평면 객체다 — hraSurvey / stressSurvey 와 같은 최상위 위치에서 읽는다.
 * (실측 2026-08-13: 인증되면 최상위 키 41개, questionnaire 가 hraSurvey/stressSurvey 와 나란히 온다)
 *
 * authenticated 를 message 로 판정하지 않는 이유:
 * 서버가 유효한 토큰에도 message="Failed" 를 주던 시기가 있었다(같은 날 회귀·복구).
 * 문자열은 언제든 다시 바뀔 수 있으므로 응답에 담긴 신원으로 판정한다(utils/authCheck.ts).
 *
 * authenticated 가 왜 중요한가:
 * 인증이 거부되면 응답이 `{ message }` 하나뿐이라 questionnaire 키도 당연히 없다.
 * 그때의 undefined 는 "문진 미제출"이 아니라 **"판정 불가"** 다. 이 둘을 구분하지 않으면
 * 일시적인 인증 실패마다 문진을 미작성으로 오판해 다시 띄우게 되고,
 * 사용자가 다시 작성하면 중복 레코드가 쌓인다(AP_009_001 에 중복 방지가 없다).
 */
export const fetchSurveyStatus = async (): Promise<SurveyStatus> => {
  const token = window.localStorage.getItem('token') ?? '';
  if (token === '') throw userFacingError('로그인 정보가 없습니다.');

  const res = await api.post('permission/isAuth', { token });
  const d = res.data ?? {};
  return {
    authenticated: !shouldForceLogout(
      d,
      util.strNull(window.localStorage.getItem('userID')),
      util.strNull(window.localStorage.getItem('userCusID')),
    ),
    questionnaireId: toQuestionnaireId(d.questionnaire),
  };
};

/** 캐시 키가 customerId 이므로 계정이 바뀌면 이전 계정의 제출 여부를 재사용하지 않는다. */
const statusResource = createCachedResource(fetchSurveyStatus, currentCustomerId);

export const getSurveyStatus = (force = false) => statusResource.get(force);

/** 문진 저장 뒤에 호출해 제출 여부를 다시 읽는다. */
export const invalidateSurveyStatus = () => statusResource.invalidate();

export type UseSurveyStatus = UseResource<SurveyStatus>;

export const useSurveyStatus = (): UseSurveyStatus =>
  useCachedResource(statusResource, '문진 작성 여부를 확인하지 못했습니다.');
